import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect
} from "@wingspanhq/fe-component-library";
import {
  IWorkDefinition,
  IWorkItem
} from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationUpdateWorkItem } from "../../../../query/workItem/mutations/useMutationUpdateWorkItem";
import { FormPartialAttributes } from "../../components/FormPartialAttributes";
import { AcceptedValuesResources } from "../../components/FormPartialAttributes/helpers";
import { getAttributeValidators } from "../../components/FormPartialAttributes/validation";
import { PublicCommentField } from "../../components/PublicCommentField";
import { stripNullAttributes } from "../../utils/stripNullAttributes";

type Props = {
  workItem: IWorkItem;
  workDefinition: IWorkDefinition;
  resources: AcceptedValuesResources;
  clientName?: string;
} & WSModalCustomProps<any>;

export const ModalEditItem: React.FC<Props> = ({
  workItem,
  workDefinition,
  resources,
  clientName,
  ...modalProps
}) => {
  const payerCommentExists = !!(workItem as any)?.payerOwnedData?.comment;

  const validationSchema = useMemo(() => {
    const attributeValidators = getAttributeValidators(
      workDefinition.attributeDefinitions
    );

    return Yup.object().shape({
      ...attributeValidators,
      ...(payerCommentExists
        ? {
            comment: Yup.string().required("A comment is required when editing")
          }
        : {})
    });
  }, [workDefinition, payerCommentExists]);

  const [mutate, meta] = useMutationUpdateWorkItem();

  const { openSnackbar } = useWSSnackbar();

  const commentDescription = clientName
    ? `Comment is visible to both you and the client. Please provide specific details that will inform ${clientName} about the edits.`
    : "Comment is visible to both you and the client. Please provide specific details about the edits.";

  return (
    <WSForm
      defaultValues={{
        ...workItem.attributes,
        ...(payerCommentExists ? { comment: "" } : {})
      }}
      validationSchema={validationSchema}
      onSubmit={({ comment, ...attributes }) => {
        mutate(
          {
            workItemId: workItem.workItemId,
            request: {
              attributes: stripNullAttributes(attributes),
              ...(payerCommentExists && comment
                ? { payeeOwnedData: { comment } }
                : {})
            } as any
          },
          {
            onSuccess: () => {
              modalProps.onClose(undefined);

              openSnackbar({
                icon: {
                  name: "check"
                },
                message: "Work item updated"
              });
            }
          }
        );
      }}
    >
      <WSModal
        title="Edit item"
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              type: "button",
              onClick: () => {
                modalProps.onClose(undefined);
              }
            },
            {
              kind: "Primary",
              label: "Save changes",
              loading: meta.isLoading
            }
          ]
        }}
        {...modalProps}
      >
        <WSList gap="L">
          <WSSelect
            disabled
            value={workDefinition.workDefinitionId}
            onChange={() => {
              // Do nothing, fields should be permanently disabled
            }}
            label="Work item"
            required
            options={[
              {
                value: workDefinition.workDefinitionId || "",
                label: workDefinition.name || ""
              }
            ]}
          />

          <FormPartialAttributes
            attributeDefinitions={workDefinition.attributeDefinitions}
            resources={resources}
          />

          {payerCommentExists && (
            <PublicCommentField
              label="Comment *"
              description={commentDescription}
              placeholder="Provide specific details about the edits"
            />
          )}

          <WSErrorMessage error={meta.error} contextKey="WorkItem" />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalEditItem = () => useWSModalCustom(ModalEditItem);
